import { DataQualityGrade } from '@lune-climate/lune'
import { DataQualityAggregatedTimeseries, DataQualityOverallScores } from '@lune-fe/dapi-models'
import {
    DataQualityScoreCircle,
    getDataQualityScoreColor,
    getDataQualityScoreLabel,
} from '@lune-fe/lune-components-lib'
import { IconButton, Text, Tooltip } from '@lune-fe/lune-ui-lib'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import Box from '@mui/material/Box'
import { FC } from 'react'

const DataHealthScore: FC<{
    overallScores?: DataQualityOverallScores
    aggregatedScoreData?: DataQualityAggregatedTimeseries
    /**
     *   When true, renders the component in a vertical layout
     *   When false (default), renders horizontally with elements side-by-side.
     *   Defaults to false when not set
     */
    vertical?: boolean
    /**
     * When true, uses the shipped-only data quality score (shippedOverallScore) instead of the overall score.
     * This is needed for contexts like the Analytics Dashboard where we want to show data quality metrics
     * specific to shipped/fulfilled orders, excluding estimates that haven't been shipped yet.
     * When false or undefined (default), uses the overall data quality score (overallScore) which includes
     * all estimates regardless of shipping status.
     * Defaults to false when not set.
     */
    shippedOnly?: boolean
    // eslint-disable-next-line complexity
}> = ({ overallScores, aggregatedScoreData, vertical, shippedOnly }) => {
    const overallScore = overallScores?.overallScore?.score
        ? (overallScores.overallScore.score as DataQualityGrade)
        : undefined
    const shippedOverallScore = overallScores?.shippedOverallScore?.score
        ? (overallScores.shippedOverallScore.score as DataQualityGrade)
        : undefined
    const score = shippedOnly ? shippedOverallScore : overallScore

    const dataForTheBreakdown = aggregatedScoreData?.byShipmentLegs
    const totalLegs = dataForTheBreakdown?.legs ?? 0
    const timeseries = dataForTheBreakdown?.timeseries ?? []

    const totals = timeseries.reduce(
        (acc, dataPoint) => ({
            a: acc.a + dataPoint.a,
            b: acc.b + dataPoint.b,
            c: acc.c + dataPoint.c,
            d: acc.d + dataPoint.d,
        }),
        { a: 0, b: 0, c: 0, d: 0 },
    )

    const breakDownPercentages: {
        a: number
        b: number
        c: number
        d: number
    } = {
        a: totalLegs > 0 ? (totals.a / totalLegs) * 100 : 0,
        b: totalLegs > 0 ? (totals.b / totalLegs) * 100 : 0,
        c: totalLegs > 0 ? (totals.c / totalLegs) * 100 : 0,
        d: totalLegs > 0 ? (totals.d / totalLegs) * 100 : 0,
    }

    const hasData = score !== undefined

    const MiniBar: FC<{ empty: boolean }> = ({ empty }) => {
        return (
            <Box
                sx={{
                    width: '100%',
                    height: '12px',
                    display: 'flex',
                    alignItems: 'center',
                }}
            >
                {empty ? (
                    <Box
                        sx={{
                            width: `100%`,
                            backgroundColor: `Surface300`,
                            height: '100%',
                        }}
                    />
                ) : (
                    <>
                        <Box
                            sx={{
                                width: `${breakDownPercentages.d}%`,
                                backgroundColor: getDataQualityScoreColor(DataQualityGrade.D),
                                height: '100%',
                            }}
                        />
                        <Box
                            sx={{
                                width: `${breakDownPercentages.c}%`,
                                backgroundColor: getDataQualityScoreColor(DataQualityGrade.C),
                                height: '100%',
                            }}
                        />
                        <Box
                            sx={{
                                width: `${breakDownPercentages.b}%`,
                                backgroundColor: getDataQualityScoreColor(DataQualityGrade.B),
                                height: '100%',
                            }}
                        />
                        <Box
                            sx={{
                                width: `${breakDownPercentages.a}%`,
                                backgroundColor: getDataQualityScoreColor(DataQualityGrade.A),
                                height: '100%',
                            }}
                        />
                    </>
                )}
            </Box>
        )
    }

    return (
        <Box
            sx={{
                width: '100%',
                display: 'flex',
                gap: 3,
                ...(vertical
                    ? {
                          alignItems: 'flex-start',
                          flexDirection: 'column',
                          justifyContent: 'space-between',
                      }
                    : {
                          alignItems: 'center',
                          flexDirection: 'row',
                      }),
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    gap: 3,
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    width: '100%',
                    flex: 1,
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        '> div': {
                            // DataQualityScoreCircle has three possible sizes 48px, 56px or 104px
                            // This is a workaround to match the 72px dimensions from the design
                            // without changing the DataQualityScoreCircle component
                            transform: vertical ? 'scale(1.42)' : 'scale(1.286)',
                        },
                        ...(vertical
                            ? {
                                  height: '80px',
                                  width: '80px',
                                  flex: '1 0 80px',
                              }
                            : {
                                  height: '72px',
                                  width: '72px',
                                  flex: '1 0 72px',
                              }),
                    }}
                >
                    <DataQualityScoreCircle
                        empty={!hasData}
                        score={score ?? DataQualityGrade.D}
                        size={'md'}
                    />
                </Box>
                {vertical && (
                    <Box
                        sx={{
                            width: '100%',
                        }}
                    >
                        {!hasData ? (
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    width: '100%',
                                }}
                            >
                                <Text variant={'body2-medium'}>Not enough data</Text>
                                <Tooltip
                                    title="Data health needs a few more data points to take shape."
                                    placement="top"
                                    contentWidth={true}
                                >
                                    <IconButton
                                        leadingIcon={<InfoOutlinedIcon />}
                                        variant={'secondary'}
                                    />
                                </Tooltip>
                            </Box>
                        ) : (
                            <>
                                <Text variant={'h5'}>{getDataQualityScoreLabel(score)}</Text>
                                <Text variant={'body2'} color={`TextSecondary`}>
                                    Data Quality
                                </Text>
                            </>
                        )}
                    </Box>
                )}
            </Box>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    width: '100%',
                }}
            >
                {!vertical && (
                    <Text variant={'body2-medium'}>
                        {!hasData ? 'Not enough data' : getDataQualityScoreLabel(score)}
                    </Text>
                )}
                <Box
                    sx={{
                        width: '100%',
                        gap: 2,
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <MiniBar empty={!hasData} />
                </Box>
            </Box>
        </Box>
    )
}

export default DataHealthScore
